import { useMemo } from "react";

import {
  deriveEarlyRepaymentPlan,
  type IEarlyRepaymentPlan,
} from "./earlyRepayment";
import type { IRepaymentPlanData, IRepaymentPlanEntryData } from "./types";

export type MoneyColumn =
  | "payment"
  | "interest"
  | "principal"
  | "fees"
  | "remainingBalance";

export interface IRepaymentPlanDisplay {
  displayPlan: IEarlyRepaymentPlan;
  hasFees: boolean;
  // Administration + setup, excluding the one-off settlement fee. Gates the
  // CHART's fee series only — it plots the setup fee as month 0's bar but
  // never the settlement figure. NOT the gate for "fees saved", which tracks
  // `feesSaved` (skipped months' administration fees only, so a setup-fee-only
  // bid saved nothing) and uses `hasAdministrationFee` instead.
  hasRecurringFees: boolean;
  moneyColumns: MoneyColumn[];
  recurringFees: (entry: IRepaymentPlanEntryData) => number;
  cellValue: (entry: IRepaymentPlanEntryData, key: MoneyColumn) => number;
  totalValue: (key: MoneyColumn) => number | null;
  // The truncated final row's early-settlement fee, surfaced only once the
  // slider has been used. A fee of exactly 0 is a real answer, so callers must
  // check `!== null`, never truthiness.
  settlementFee: number | null;
}

const EMPTY_DISPLAY: IRepaymentPlanDisplay = {
  displayPlan: {
    entries: [],
    totals: {
      totalPayment: "0",
      totalInterest: "0",
      totalPrincipal: "0",
      totalAdministrationFee: "0",
      setupFee: "0",
      totalRepaid: "0",
      totalCostOfCredit: "0",
    },
    isEarly: false,
    monthsSaved: 0,
    settlementFee: null,
    netSaved: 0,
    interestSaved: 0,
    feesSaved: 0,
    grossSaved: 0,
  },
  hasFees: false,
  hasRecurringFees: false,
  moneyColumns: ["payment", "interest", "principal", "remainingBalance"],
  recurringFees: () => 0,
  cellValue: () => 0,
  totalValue: () => null,
  settlementFee: null,
};

export const useRepaymentPlanDisplay = (
  plan: IRepaymentPlanData | null | undefined,
  months: number,
  // True once the user has touched the slider — including dragging it back
  // to the full term. Deliberately NOT `months < fullMonths`: that can't
  // distinguish "untouched" from "dragged to the full term", and only the
  // former should hide the settlement fee.
  isTruncated: boolean,
): IRepaymentPlanDisplay => {
  return useMemo(() => {
    if (!plan) return EMPTY_DISPLAY;

    const fullMonths = plan.entries.length - 1;
    const clampedMonths = Math.min(Math.max(months, 1), fullMonths);
    const displayPlan = deriveEarlyRepaymentPlan(plan, clampedMonths);

    // Index access rather than .at(-1): one consuming app's TS lib target
    // predates ES2022, where Array.prototype.at does not exist.
    const finalEntry =
      displayPlan.entries[displayPlan.entries.length - 1] ?? null;
    const settlementFee =
      isTruncated && finalEntry?.earlySettlementFee != null
        ? Number(finalEntry.earlySettlementFee)
        : null;

    const hasRecurringFees =
      plan.hasAdministrationFee || Number(plan.totals.setupFee) > 0;
    const hasFees = hasRecurringFees || settlementFee !== null;

    const moneyColumns: MoneyColumn[] = [
      "payment",
      "interest",
      "principal",
      ...(hasFees ? (["fees"] as const) : []),
      "remainingBalance",
    ];

    const recurringFees = (entry: IRepaymentPlanEntryData): number =>
      Number(entry.administrationFees);

    const cellValue = (
      entry: IRepaymentPlanEntryData,
      key: MoneyColumn,
    ): number => {
      switch (key) {
        case "fees":
          return (
            recurringFees(entry) +
            (entry.month === 0 ? Number(displayPlan.totals.setupFee) : 0) +
            (settlementFee !== null && entry.month === finalEntry?.month
              ? settlementFee
              : 0)
          );
        case "payment":
          return Number(entry.payment);
        default:
          return Number(entry[key]);
      }
    };

    const totalValue = (key: MoneyColumn): number | null => {
      const totalRecurringFees = Number(
        displayPlan.totals.totalAdministrationFee,
      );
      switch (key) {
        case "payment":
          return Number(displayPlan.totals.totalPayment);
        case "interest":
          return Number(displayPlan.totals.totalInterest);
        case "principal":
          return Number(displayPlan.totals.totalPrincipal);
        case "fees":
          return (
            totalRecurringFees +
            Number(displayPlan.totals.setupFee) +
            (settlementFee ?? 0)
          );
        default:
          return null;
      }
    };

    return {
      displayPlan,
      hasFees,
      hasRecurringFees,
      moneyColumns,
      recurringFees,
      cellValue,
      totalValue,
      settlementFee,
    };
  }, [plan, months, isTruncated]);
};
